import CloseIcon from "@mui/icons-material/Close";
import { Drawer, Grid, IconButton, useTheme } from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import AppointmentDetails from "./AppointmentDetails";
import ClientDetails from "./ClientDetails";
import { handleOpenSnackBar } from "../../../utils";
import { getAppointmentDetail } from "../../../services/common/appointmentApiFn";
import AppLoader from "../../../components/AppLoader";
import AppointmentClientDetailSkeleton from "../../../components/Skeleton/AppoinmentClientDetailSkeleton";
import AppointmentDetailsSkeleton from "../../../components/Skeleton/AppointmentDetailSkeleton";
import {
  updateAppointmentStatus,
  updateAppointmentWorkerStatus,
} from "../../../services/company/appointmentApiFn";
import ChatDialog from "./ChatDialog";
import io from "socket.io-client";
import { socketUrl } from "../../../services";

const socket = io.connect(socketUrl);

const drawerComponentStyle = {
  "& .MuiDrawer-paper": {
    width: { xs: "100%", md: "50%" },
  },
  "& .MuiDrawer-paper .drawer-close-icon": {
    position: "fixed",
    top: 16,
    left: "46%",
    zIndex: 1300,
    backgroundColor: "custom.white",
    boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
  },
};

const Appointment = forwardRef(({ selectedEvent }, ref) => {
  const { palette } = useTheme();
  const openChatDialog = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [appointmentDetail, setAppointmentDetail] = useState(null);
  const [dialogData, setDialogData] = useState(null);

  useEffect(() => {
    if (dialogData) {
      fetchAppointmentDetail();
    }
  }, [dialogData]);

  const fetchAppointmentDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getAppointmentDetail(dialogData?.data?._id);
      if (response?.data.status === "success") {
        setAppointmentDetail(response.data.data);
      } else {
        handleOpenSnackBar(response?.data.message, "error");
      }
    } catch (error) {
      handleOpenSnackBar(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openDrawer: (params) => {
      setIsOpen(true);
      setDialogData(params);
    },
    closeDrawer: () => handleClose(),
  }));

  const handleClose = () => {
    setDialogData(null);
    setAppointmentDetail(null);
    setIsOpen(false);
  };

  const updateWorkerStatus = async (status, reason) => {
    try {
      let payload = {
        status,
        ...(status === "rejected" && {
          companyId: dialogData?.data?.companyId,
        }),
        ...(status === "rejected" && { workerId: dialogData?.data?.workerId }),
        ...(status === "rejected" && { reason }),
      };

      setStatusLoading(true);
      const response = await updateAppointmentWorkerStatus(
        dialogData?.data?._id,
        payload
      );
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchAppointmentDetail();
        dialogData?.getData();
        if (status === "rejected") {
          handleClose();
        }
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setStatusLoading(false);
    }
  };

  const updateAppointStatus = async (status) => {
    try {
      setStatusLoading(true);
      const response = await updateAppointmentStatus(dialogData?.data?._id, {
        status,
      });
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchAppointmentDetail();
        dialogData?.getData();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={statusLoading} />
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => handleClose()}
        sx={{ ...drawerComponentStyle }}
      >
        <IconButton className="drawer-close-icon" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>

        <Grid container height="100vh">
          <Grid item xs={12} md={4.5}>
            {isLoading && !appointmentDetail ? (
              <AppointmentClientDetailSkeleton />
            ) : (
              <ClientDetails
                userDetails={appointmentDetail?.userId}
                onChatClick={() =>
                  openChatDialog.current.openDialog(appointmentDetail)
                }
              />
            )}
          </Grid>
          <Grid
            item
            xs={12}
            md={7.5}
            sx={{ borderLeft: { md: `1px solid ${palette.primary.main}` } }}
          >
            {isLoading && !appointmentDetail ? (
              <AppointmentDetailsSkeleton />
            ) : (
              <AppointmentDetails
                appointmentDetails={appointmentDetail}
                setIsOpen={setIsOpen}
                updateWorkerStatus={updateWorkerStatus}
                updateAppointStatus={updateAppointStatus}
                handleCloseMainDrawer={handleClose}
              />
            )}
          </Grid>
        </Grid>
        <ChatDialog ref={openChatDialog} socket={socket} />
      </Drawer>
    </>
  );
});

export default Appointment;
